/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { MessageCircle, Check, X, RefreshCw, Lock, Info } from "lucide-react";
import apicache from "../clearCach/clear";
import { useTranslation } from "react-i18next";

function FoorchatSetting({ onDeactivate }) {
  const store = useSelector((state) => state.StoreReducer);
  const [loading, setLoading] = useState(false);
  const [isChatBotActive, setIsChatBotActive] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const [data, setData] = useState({
    chatBotSettings: {
      TrackOrder: true,
      AcceptAudio: false,
      AcceptImages: false,
      MessageAfterConfimation: true,
      ShowDiscount: true,
      ShowDiscountNotConfirm: false,
      Botnotification: false,
    },
  });

  useEffect(() => {
    const initSettings = async () => {
      if (!store?.id) return;

      setData({
        ...store,
        chatBotSettings: store.chatBotSettings || {
          TrackOrder: true,
          AcceptAudio: false,
          AcceptImages: false,
          MessageAfterConfimation: true,
          ShowDiscount: true,
          ShowDiscountNotConfirm: false,
          Botnotification: false,
        },
      });

      try {
        const storeSnap = await getDoc(doc(db, "store", store.id));
        if (storeSnap.exists()) {
          const latest = storeSnap.data();
          const enabled = Boolean(latest?.foorchat?.enabled);

          setData((prev) => ({
            ...prev,
            foorchat: {
              ...(prev.foorchat || {}),
              enabled,
            },
          }));
          setIsChatBotActive(enabled);
        }
      } catch (error) {
        console.error("Error fetching latest foorchat status:", error);
        // fallback to Redux value
        setIsChatBotActive(Boolean(store.foorchat?.enabled));
      }
    };

    initSettings();
  }, [store?.id]);

  // Function to update chatbot settings
  const updateChatBotSetting = (settingName, value) => {
    setData((prev) => ({
      ...prev,
      chatBotSettings: {
        ...prev.chatBotSettings,
        [settingName]: value,
      },
    }));
    setIsDirty(true);
  };

  const showAlert = (
    icon,
    title,
    text,
    showCancelButton = false,
    confirmButtonText = t("servicePageActivation.foorchat.alerts.okButton"),
  ) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText,
      cancelButtonText: t("servicePageActivation.foorchat.settingchat.annuler"),
      showCancelButton,
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `rgba(0,0,0,0.7)`,
      position: "center",
      customClass: {
        popup: "swal-popup-on-top",
        backdrop: "swal-backdrop-on-top",
        container: "swal-container-on-top",
      },
      willOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = "9999999999";
      },
    });
  };

  // Add this style to ensure SweetAlert appears on top
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .swal-container-on-top {
        z-index: 9999999999 !important;
      }
      .swal-backdrop-on-top {
        background: rgba(0,0,0,0.7) !important;
      }
      .swal-popup-on-top {
        position: relative;
        box-shadow: 0 0 20px rgba(0,0,0,0.2);
      }
      body.swal2-shown {
        overflow: hidden;
      }
      body.swal2-shown > [aria-hidden="true"] {
        filter: blur(3px);
      }
    `;
    document.head.appendChild(style);

    const handleDocumentClick = () => {
      const swalContainer = document.querySelector(".swal2-container");
      if (swalContainer) {
        swalContainer.style.zIndex = "9999999999";
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      document.head.removeChild(style);
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  const handleDChange = () => {
    setData({
      ...data,
      foorchat: { ...data.foorchat, enabled: !data.foorchat?.enabled },
    });
    setIsDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!data?.id) {
      await showAlert(
        "error",
        t("servicePageActivation.foorchat.settingchat.errors.title"),
        t("servicePageActivation.foorchat.settingchat.errors.storeIdMissing"),
      );
      return;
    }

    try {
      setLoading(true);

      const newEnabled = Boolean(data.foorchat?.enabled);

      await setDoc(
        doc(db, "store", data.id),
        {
          foorchat: {
            ...(data.foorchat || {}),
            enabled: newEnabled,
          },
          chatBotSettings: data.chatBotSettings || {
            TrackOrder: true,
            AcceptAudio: false,
            AcceptImages: false,
            MessageAfterConfimation: true,
            ShowDiscount: true,
            ShowDiscountNotConfirm: false,
            Botnotification: false,
          },
        },
        { merge: true },
      );

      setLoading(false);
      setIsChatBotActive(newEnabled);
      setIsDirty(false);

      await showAlert(
        "success",
        t("servicePageActivation.foorchat.alerts.saveSuccess"),
      );

      if (!newEnabled) {
        onDeactivate?.();
      }
    } catch (error) {
      console.error("Save error:", error);
      setLoading(false);
      await showAlert(
        "error",
        t("servicePageActivation.foorchat.settingchat.errors.title"),
        t("servicePageActivation.foorchat.settingchat.errors.saveFailed"),
      );
    }
  };

  return (
    <div className="min-h-[400px] h-fit p-6">
      <div className="bg-white dark:!bg-[#181819] dark:!border-[#3c3a3d] rounded-xl shadow-sm p-6 border border-[#fbf4ff]">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-[#9e68ba] mb-2 flex items-center">
            <MessageCircle className="ml-2" size={20} />
            {t("servicePageActivation.foorchat.settingchat.title")}
          </h3>
          <p className="text-gray-600 dark:!text-gray-400">
            {t("servicePageActivation.foorchat.settingchat.description")}
          </p>
        </div>

        {/* Service Status Indicator */}
        <div
          className={`mb-4 p-3 rounded-lg border ${
            isChatBotActive
              ? "border-green-200 bg-white dark:!bg-[#181819] dark:!border-green-700"
              : "border-gray-200 bg-white dark:!bg-[#181819] dark:!border-[#3c3a3d]"
          }`}
        >
          <div className="flex items-center gap-x-2">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                isChatBotActive ? "bg-green-500" : "bg-gray-400"
              }`}
            ></div>
            <span
              className={`text-sm font-medium dark:!text-gray-300 ${
                isChatBotActive
                  ? "text-green-700 dark:!text-green-300"
                  : "text-gray-700 dark:!text-gray-400"
              }`}
            >
              {isChatBotActive
                ? t("servicePageActivation.foorchat.settingchat.statusActive")
                : t(
                    "servicePageActivation.foorchat.settingchat.statusInactive",
                  )}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Toggle */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium dark:!text-gray-300 text-[#9e68ba]">
                {t("servicePageActivation.foorchat.settingchat.enableLabel")}
              </label>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium dark:!text-gray-300 ${
                  data.foorchat?.enabled
                    ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                    : "bg-gray-100 text-gray-800 dark:!bg-[#3c3a3d] dark:!text-gray-400"
                }`}
              >
                {data.foorchat?.enabled
                  ? t("servicePageActivation.foorchat.settingchat.enabled")
                  : t("servicePageActivation.foorchat.settingchat.disabled")}
              </div>
            </div>

            {/* Main Chat Toggle */}
            <div
              className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                data.foorchat?.enabled
                  ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                  : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
              }`}
              onClick={handleDChange}
            >
              <div className="relative inline-block w-12 ml-3 align-middle select-none">
                <input
                  type="checkbox"
                  checked={data.foorchat?.enabled}
                  onChange={() => {}}
                  className="hidden"
                  id="toggle-main"
                />
                <label
                  htmlFor="toggle-main"
                  className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                    data.foorchat?.enabled ? "bg-purple-400" : ""
                  }`}
                >
                  <span
                    className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                      data.foorchat?.enabled
                        ? "rtl:-translate-x-6 ltr:translate-x-6"
                        : "translate-x-0"
                    }`}
                  />
                </label>
              </div>

              <div>
                <div className="flex items-center">
                  {data.foorchat?.enabled ? (
                    <MessageCircle size={18} className="ml-2 text-[#9e68ba]" />
                  ) : (
                    <X
                      size={18}
                      className="ml-2 text-gray-500 dark:!text-gray-400"
                    />
                  )}
                  <p className="font-medium dark:!text-gray-300">
                    {data.foorchat?.enabled
                      ? t(
                          "servicePageActivation.foorchat.settingchat.chatbotEnabled",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.chatbotDisabled",
                        )}
                  </p>
                </div>
                <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                  {data.foorchat?.enabled
                    ? t(
                        "servicePageActivation.foorchat.settingchat.chatbotCanInteract",
                      )
                    : t(
                        "servicePageActivation.foorchat.settingchat.chatbotNotAvailable",
                      )}
                </p>
              </div>
            </div>
          </div>

          {/* Advanced Chatbot Settings */}
          {data.foorchat?.enabled && (
            <div className="space-y-4 border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-lg font-semibold text-[#9e68ba] mb-4">
                {t(
                  "servicePageActivation.foorchat.settingchat.advancedSettings",
                )}
              </h4>

              {/* Track Orders */}
              <div
                className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                  data.chatBotSettings?.TrackOrder
                    ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                    : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                }`}
                onClick={() =>
                  updateChatBotSetting(
                    "TrackOrder",
                    !data.chatBotSettings?.TrackOrder,
                  )
                }
              >
                <div className="relative inline-block w-12 ml-3 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={data.chatBotSettings?.TrackOrder || false}
                    onChange={() => {}}
                    className="hidden"
                    id="toggle-trackorder"
                  />
                  <label
                    htmlFor="toggle-trackorder"
                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                      data.chatBotSettings?.TrackOrder ? "bg-purple-400" : ""
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                        data.chatBotSettings?.TrackOrder
                          ? "rtl:-translate-x-6 ltr:translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        data.chatBotSettings?.TrackOrder ? "#9e68ba" : "#9ca3af"
                      }
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2"
                    >
                      <path d="M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 0 1-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0 1 12 0" />
                      <circle cx="12" cy="8" r="2" />
                      <path d="M8.714 14h-3.71a1 1 0 0 0-.948.683l-2.004 6A1 1 0 0 0 3 22h18a1 1 0 0 0 .948-1.316l-2-6a1 1 0 0 0-.949-.684h-3.712" />
                    </svg>
                    <p className="font-medium dark:!text-gray-300">
                      {data.chatBotSettings?.TrackOrder
                        ? t(
                            "servicePageActivation.foorchat.settingchat.trackOrderEnabled",
                          )
                        : t(
                            "servicePageActivation.foorchat.settingchat.trackOrderDisabled",
                          )}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                    {data.chatBotSettings?.TrackOrder
                      ? t(
                          "servicePageActivation.foorchat.settingchat.trackOrderCanTrack",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.trackOrderCannotTrack",
                        )}
                  </p>
                </div>
              </div>

              {/* Accept Audio Messages */}
              <div
                className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                  data.chatBotSettings?.AcceptAudio
                    ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                    : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                }`}
                onClick={() =>
                  updateChatBotSetting(
                    "AcceptAudio",
                    !data.chatBotSettings?.AcceptAudio,
                  )
                }
              >
                <div className="relative inline-block w-12 ml-3 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={data.chatBotSettings?.AcceptAudio || false}
                    onChange={() => {}}
                    className="hidden"
                    id="toggle-audio"
                  />
                  <label
                    htmlFor="toggle-audio"
                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                      data.chatBotSettings?.AcceptAudio ? "bg-purple-400" : ""
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                        data.chatBotSettings?.AcceptAudio
                          ? "rtl:-translate-x-6 ltr:translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        data.chatBotSettings?.AcceptAudio
                          ? "#9e68ba"
                          : "#9ca3af"
                      }
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2"
                    >
                      <path d="M2 10v3" />
                      <path d="M6 6v11" />
                      <path d="M10 3v18" />
                      <path d="M14 8v7" />
                      <path d="M18 5v13" />
                      <path d="M22 10v3" />
                    </svg>
                    <p className="font-medium dark:!text-gray-300">
                      {data.chatBotSettings?.AcceptAudio
                        ? t(
                            "servicePageActivation.foorchat.settingchat.acceptAudioEnabled",
                          )
                        : t(
                            "servicePageActivation.foorchat.settingchat.acceptAudioDisabled",
                          )}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                    {data.chatBotSettings?.AcceptAudio
                      ? t(
                          "servicePageActivation.foorchat.settingchat.acceptAudioCanSend",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.acceptAudioRequestText",
                        )}
                  </p>
                </div>
              </div>

              {/* Accept Images */}
              <div
                className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                  data.chatBotSettings?.AcceptImages
                    ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                    : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                }`}
                onClick={() =>
                  updateChatBotSetting(
                    "AcceptImages",
                    !data.chatBotSettings?.AcceptImages,
                  )
                }
              >
                <div className="relative inline-block w-12 ml-3 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={data.chatBotSettings?.AcceptImages || false}
                    onChange={() => {}}
                    className="hidden"
                    id="toggle-images"
                  />
                  <label
                    htmlFor="toggle-images"
                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                      data.chatBotSettings?.AcceptImages ? "bg-purple-400" : ""
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                        data.chatBotSettings?.AcceptImages
                          ? "rtl:-translate-x-6 ltr:translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        data.chatBotSettings?.AcceptImages
                          ? "#9e68ba"
                          : "#9ca3af"
                      }
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2"
                    >
                      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                    <p className="font-medium dark:!text-gray-300">
                      {data.chatBotSettings?.AcceptImages
                        ? t(
                            "servicePageActivation.foorchat.settingchat.acceptImagesEnabled",
                          )
                        : t(
                            "servicePageActivation.foorchat.settingchat.acceptImagesDisabled",
                          )}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                    {data.chatBotSettings?.AcceptImages
                      ? t(
                          "servicePageActivation.foorchat.settingchat.acceptImagesCanSend",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.acceptImagesCannotSend",
                        )}
                  </p>
                </div>
              </div>

              {/* Messages After Order Confirmation */}
              <div
                className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                  data.chatBotSettings?.MessageAfterConfimation
                    ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                    : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                }`}
                onClick={() =>
                  updateChatBotSetting(
                    "MessageAfterConfimation",
                    !data.chatBotSettings?.MessageAfterConfimation,
                  )
                }
              >
                <div className="relative inline-block w-12 ml-3 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={
                      data.chatBotSettings?.MessageAfterConfimation || false
                    }
                    onChange={() => {}}
                    className="hidden"
                    id="toggle-message-after"
                  />
                  <label
                    htmlFor="toggle-message-after"
                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                      data.chatBotSettings?.MessageAfterConfimation
                        ? "bg-purple-400"
                        : ""
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                        data.chatBotSettings?.MessageAfterConfimation
                          ? "rtl:-translate-x-6 ltr:translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        data.chatBotSettings?.MessageAfterConfimation
                          ? "#9e68ba"
                          : "#9ca3af"
                      }
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <p className="font-medium dark:!text-gray-300">
                      {data.chatBotSettings?.MessageAfterConfimation
                        ? t(
                            "servicePageActivation.foorchat.settingchat.messageAfterConfirmationEnabled",
                          )
                        : t(
                            "servicePageActivation.foorchat.settingchat.messageAfterConfirmationDisabled",
                          )}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                    {data.chatBotSettings?.MessageAfterConfimation
                      ? t(
                          "servicePageActivation.foorchat.settingchat.messageAfterConfirmationContinue",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.messageAfterConfirmationStop",
                        )}
                  </p>
                </div>
              </div>

              {/* Show Discounts */}
              <div
                className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                  data.chatBotSettings?.ShowDiscount
                    ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                    : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                }`}
                onClick={() =>
                  updateChatBotSetting(
                    "ShowDiscount",
                    !data.chatBotSettings?.ShowDiscount,
                  )
                }
              >
                <div className="relative inline-block w-12 ml-3 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={data.chatBotSettings?.ShowDiscount || false}
                    onChange={() => {}}
                    className="hidden"
                    id="toggle-discount"
                  />
                  <label
                    htmlFor="toggle-discount"
                    className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                      data.chatBotSettings?.ShowDiscount ? "bg-purple-400" : ""
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                        data.chatBotSettings?.ShowDiscount
                          ? "rtl:-translate-x-6 ltr:translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>

                <div>
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        data.chatBotSettings?.ShowDiscount
                          ? "#9e68ba"
                          : "#9ca3af"
                      }
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="ml-2"
                    >
                      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
                      <path d="m15 9-6 6" />
                      <path d="M9 9h.01" />
                      <path d="M15 15h.01" />
                    </svg>
                    <p className="font-medium dark:!text-gray-300">
                      {data.chatBotSettings?.ShowDiscount
                        ? t(
                            "servicePageActivation.foorchat.settingchat.showDiscountEnabled",
                          )
                        : t(
                            "servicePageActivation.foorchat.settingchat.showDiscountDisabled",
                          )}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                    {data.chatBotSettings?.ShowDiscount
                      ? t(
                          "servicePageActivation.foorchat.settingchat.showDiscountWillShow",
                        )
                      : t(
                          "servicePageActivation.foorchat.settingchat.showDiscountWontShow",
                        )}
                  </p>
                </div>
              </div>

              {/* Automatic Discounts Without Confirmation */}
              {data.chatBotSettings?.ShowDiscount && (
                <div
                  className={`p-4 rounded-lg border transition-colors cursor-pointer flex items-center ${
                    data.chatBotSettings?.ShowDiscountNotConfirm
                      ? "bg-[#fcf6ff] border-purple-200 dark:!bg-[#181819] dark:!border-purple-700"
                      : "bg-white border-gray-200 hover:bg-gray-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]"
                  }`}
                  onClick={() =>
                    updateChatBotSetting(
                      "ShowDiscountNotConfirm",
                      !data.chatBotSettings?.ShowDiscountNotConfirm,
                    )
                  }
                >
                  {/* Switch Button */}
                  <div className="relative inline-block w-12 ml-3 align-middle select-none">
                    <input
                      type="checkbox"
                      checked={
                        data.chatBotSettings?.ShowDiscountNotConfirm || false
                      }
                      onChange={() => {}}
                      className="hidden"
                      id="toggle-discount-noconfirm"
                    />
                    <label
                      htmlFor="toggle-discount-noconfirm"
                      className={`block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer ${
                        data.chatBotSettings?.ShowDiscountNotConfirm
                          ? "bg-purple-400"
                          : ""
                      }`}
                    >
                      <span
                        className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in ${
                          data.chatBotSettings?.ShowDiscountNotConfirm
                            ? "rtl:-translate-x-6 ltr:translate-x-6"
                            : "translate-x-0"
                        }`}
                      />
                    </label>
                  </div>

                  {/* Content */}
                  <div>
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={
                          data.chatBotSettings?.ShowDiscountNotConfirm
                            ? "#9e68ba"
                            : "#9ca3af"
                        }
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M16 12l-4-4-4 4" />
                        <path d="M12 16V8" />
                      </svg>
                      <p className="font-medium dark:!text-gray-300">
                        {t(
                          "servicePageActivation.foorchat.settingchat.showDiscountAutomatic",
                        )}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 dark:!text-gray-400 mt-1">
                      {t(
                        "servicePageActivation.foorchat.settingchat.showDiscountAutomaticDesc",
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Important Notes Section */}
          <div className="p-4 rounded-lg border border-yellow-100 bg-yellow-50 dark:!bg-[#181819] dark:!border-[#3c3a3d]">
            <h4 className="font-medium dark:!text-gray-300 text-lg text-yellow-800 dark:!text-yellow-300 mb-2 flex items-center">
              <Info size={18} className="ml-2" />
              {t("servicePageActivation.foorchat.settingchat.importantNotes")}
            </h4>
            <ul className="text-sm text-gray-700 dark:!text-gray-400 space-y-2 mr-5 list-disc">
              <li>{t("servicePageActivation.foorchat.settingchat.note1")}</li>
              <li>{t("servicePageActivation.foorchat.settingchat.note2")}</li>
              <li>{t("servicePageActivation.foorchat.settingchat.note3")}</li>
              <li>{t("servicePageActivation.foorchat.settingchat.note4")}</li>
              <li>{t("servicePageActivation.foorchat.settingchat.note5")}</li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isDirty}
            className={`px-6 py-3 text-white rounded-lg transition-colors flex items-center justify-center ${
              loading || !isDirty
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#9b59bf] hover:bg-[#814a9f]"
            }`}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin ml-2" size={18} />
                {t("servicePageActivation.foorchat.settingchat.saving")}
              </>
            ) : (
              <>
                <Check className="ml-2" size={18} />
                {t("servicePageActivation.foorchat.settingchat.saveSettings")}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FoorchatSetting;
